import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { FileText, Megaphone, Search, User, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SearchHit, SearchKind, SearchResponse } from '@up2d8/contracts';
import { Button, SearchInput, cn } from '@up2d8/ui';
import { searchHitSubtitle, searchHitTarget } from './search-hit';
import { useSession } from './session';

/** Hoeveel recente zoekopdrachten er bewaard blijven. Meer is een geschiedenis, geen hulp. */
const RECENT_MAX = 5;
const RECENT_KEY = 'up2d8.recent-searches';

/**
 * Het globale zoekveld (Stap 34d).
 *
 * Eén component voor beide zones. De medewerkerszone en de beheerzone zoeken met dezelfde route en
 * krijgen andere resultaten, en dat verschil komt van de **server**: elke soort wordt daar getoetst
 * aan de rechten en de doelgroep van de vrager. Twee componenten zouden betekenen dat er twee
 * plekken zijn waar iemand per ongeluk een groep kan tonen die er niet hoort te zijn.
 *
 * Een groep die **ontbreekt** in het antwoord wordt niet getekend; een groep die er is maar leeg is,
 * krijgt "Geen … gevonden voor …". Dat onderscheid is het hele contract: "geen documenten gevonden"
 * bij iemand die nooit documenten mag zien, is een mededeling over iets wat voor hem niet bestaat.
 */
export function GlobalSearch({
  zone,
  testId = 'global-search',
}: {
  zone: 'app' | 'admin';
  /**
   * Eigen haak voor een tweede plaatsing.
   *
   * Sinds UI-5 staat het zoekveld op twee plekken in de DOM: in de kopbalk van het bureauscherm en
   * bovenaan het beginscherm van de telefoon. Er is er altijd precies één zichtbaar — CSS regelt
   * dat — maar een test ziet ze allebei, en twee elementen met dezelfde haak laten elke selector
   * omvallen. Vandaar één naam per plaats.
   */
  testId?: string;
}) {
  const { t } = useTranslation();
  const { api, has } = useSession();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [recent, setRecent] = useState<string[]>(() => readRecent());
  const container = useRef<HTMLDivElement>(null);

  // Niet op elke toetsaanslag zoeken: op een telefoon met een trage verbinding blijft de lijst
  // anders onder je vingers verspringen. Zelfde kwart seconde als bij de collega-lijst.
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term), 250);
    return () => clearTimeout(timer);
  }, [term]);

  // Buiten het veld tikken sluit de resultaten. Op een telefoon is dat de enige manier om eraf te
  // komen zonder het veld leeg te maken.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const results = useQuery({
    queryKey: ['search', debounced],
    enabled: open && debounced.trim().length >= 2,
    queryFn: async (): Promise<SearchResponse | null> => {
      const { data, error } = await api.GET('/api/v1/search', {
        params: { query: { q: debounced } },
      });
      // Een mislukte zoekopdracht hoort het scherm niet om te halen: het veld blijft staan en de
      // gebruiker kan het opnieuw proberen.
      if (error || !data) return null;
      return data as SearchResponse;
    },
  });

  if (!has('search.global.use')) return null;

  const remember = (query: string) => {
    const next = [query, ...recent.filter((entry) => entry !== query)].slice(0, RECENT_MAX);
    setRecent(next);
    writeRecent(next);
  };

  const go = (hit: SearchHit) => {
    remember(debounced.trim());
    setOpen(false);
    setTerm('');

    // De bestemming hangt aan de soort en aan de zone; een collega opent die persoon.
    void navigate(searchHitTarget(hit, zone));
  };

  const response = results.data;
  const showRecent = open && term.trim().length < 2 && recent.length > 0;

  return (
    <div ref={container} className="relative w-full max-w-sm">
      <SearchInput
        label={t('search.label')}
        placeholder={t('search.placeholder')}
        value={term}
        data-testid={testId}
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          setTerm(event.target.value);
          setOpen(true);
        }}
      />

      {(showRecent || (open && response)) && (
        <div
          data-testid="search-results"
          className="absolute left-0 right-0 z-30 mt-1 max-h-96 overflow-y-auto rounded-lg border border-border-light bg-surface shadow-lg"
        >
          {showRecent && (
            <section className="p-element">
              <div className="flex items-center justify-between gap-element">
                <p className="font-heading text-small font-semibold uppercase tracking-wide text-text-muted">
                  {t('search.recent')}
                </p>
                <Button
                  variant="ghost"
                  data-testid="search-clear-recent"
                  onClick={() => {
                    setRecent([]);
                    writeRecent([]);
                  }}
                >
                  <X aria-hidden className="mr-1 inline h-3.5 w-3.5" />
                  {t('search.clearRecent')}
                </Button>
              </div>
              <ul className="mt-1 flex flex-col">
                {recent.map((entry) => (
                  <li key={entry}>
                    <button
                      type="button"
                      data-touch-target
                      data-testid="search-recent-item"
                      onClick={() => {
                        setTerm(entry);
                        setDebounced(entry);
                      }}
                      className="flex min-h-touch w-full items-center gap-2 rounded-md px-2 text-left text-content text-text-secondary hover:bg-surface-sunk focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
                    >
                      <Search aria-hidden className="h-4 w-4 shrink-0 text-text-muted" />
                      {entry}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {open && response?.searched && (
            <>
              {/* Een groep die ontbreekt wordt niet getekend: die soort bestaat niet voor deze
                  gebruiker. Alleen een groep die er is maar leeg is, krijgt een lege staat. */}
              {response.groups.map((group) => (
                <section
                  key={group.kind}
                  data-testid={`search-group-${group.kind}`}
                  className="border-t border-border-light p-element first:border-t-0"
                >
                  <p className="font-heading text-small font-semibold uppercase tracking-wide text-text-muted">
                    {t(`search.kind.${group.kind}`)}
                  </p>

                  {group.items.length === 0 ? (
                    <p className="mt-1 text-small text-text-muted">
                      {t(`search.empty.${group.kind}`, { query: response.query })}
                    </p>
                  ) : (
                    <ul className="mt-1 flex flex-col">
                      {group.items.map((hit) => (
                        <li key={`${hit.kind}:${hit.id}`}>
                          <button
                            type="button"
                            data-touch-target
                            data-testid="search-hit"
                            onClick={() => go(hit)}
                            className="flex min-h-touch w-full items-start gap-2 rounded-md px-2 py-1 text-left hover:bg-surface-sunk focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
                          >
                            <KindIcon kind={hit.kind} />
                            <span className="min-w-0">
                              <span className="block truncate text-content text-text-primary">
                                {hit.title}
                              </span>
                              {hit.subtitle && (
                                <span className="block truncate text-small text-text-muted">
                                  {searchHitSubtitle(hit)}
                                </span>
                              )}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}

                  {group.hasMore && (
                    <p className="mt-1 text-small text-text-muted">{t('search.more')}</p>
                  )}
                </section>
              ))}

              {response.groups.length === 0 && (
                <p className="p-element text-small text-text-muted">{t('search.nothingAtAll')}</p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function KindIcon({ kind }: { kind: SearchKind }) {
  const className = cn('mt-0.5 h-4 w-4 shrink-0 text-text-muted');
  if (kind === 'post') return <Megaphone aria-hidden className={className} />;
  if (kind === 'colleague') return <User aria-hidden className={className} />;
  return <FileText aria-hidden className={className} />;
}

/**
 * De recente zoekopdrachten, lokaal in de browser.
 *
 * `localStorage` en niet de server, en dat is geen gemakzucht: een zoekgeschiedenis op de server is
 * een lijst van waar een medewerker naar zocht, en die zou een werkgever kunnen opvragen. Hier staat
 * hij op het toestel van de medewerker en verlaat hij dat nooit.
 *
 * Elke lees- en schrijfactie staat in een `try`: in een privévenster of met geblokkeerde
 * site-gegevens gooit `localStorage` een fout, en een zoekveld hoort daar niet op te vallen.
 */
function readRecent(): string[] {
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((e): e is string => typeof e === 'string') : [];
  } catch {
    return [];
  }
}

function writeRecent(entries: string[]): void {
  try {
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(entries));
  } catch {
    // Niets aan te doen, en niets om te melden: het zoeken werkt ook zonder geschiedenis.
  }
}
